/**
 * useClientOptions — Pattern A launch hook (SP13.0).
 *
 * Mirrors useCalculator's runtime: a process-global state bus that
 * <ClientOptionsHost /> subscribes to. Any caller in the tree can call
 * `launch()` to open the dialog.
 *
 * Singleton guard: a second launch while one is active no-ops with a
 * console.warn (matching useCalculator's D10 behavior).
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { ClientOptionsLaunchProps } from './types';

interface RuntimeState {
  active: ClientOptionsLaunchProps | null;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let state: RuntimeState = { active: null };

function emit() {
  listeners.forEach((l) => l());
}

function setState(next: RuntimeState) {
  state = next;
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): RuntimeState {
  return state;
}

/** Internal — exposed for ClientOptionsHost (and tests). */
export const _clientOptionsRuntime = {
  subscribe,
  getSnapshot,
  setState,
};

export function useClientOptions() {
  // Subscribe so callers re-render when the runtime state changes (e.g. to
  // disable a "Open Client Options" button while one is already open).
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const launch = useCallback((props: ClientOptionsLaunchProps = {}) => {
    if (state.active) {
      console.warn('[client-options] launch ignored — dialog already open.');
      return;
    }
    setState({ active: props });
  }, []);

  const close = useCallback(() => {
    setState({ active: null });
  }, []);

  return { launch, close, isOpen: state.active !== null };
}
